<script setup lang="ts">
import { VButton, VModal, VSection, VTextField } from '@/modules/shared/components'
import useVuelidate from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'
import { ref } from 'vue'
import { BeneficiaryTypes, Beneficiary } from '../../stores/better-individual-store'
import VSelect from '@/modules/investing/components/v-select.vue'

const props = defineProps<{
  modalStore: any
  onSubmit: (payload: any) => Promise<void>
}>()

const loading = ref(false)

const beneficiaryInitialState = {
  allocation_percentage: null,
  birthdate: null,
  name: '',
  relationship: null,
  tax_id: null,
  type: BeneficiaryTypes.Individual,
}
const beneficiaryRules = {
  allocation_percentage: { required },
  name: { required },
  type: { required },
}
const beneficiaryForm = ref({ ...beneficiaryInitialState })

const v$ = useVuelidate(beneficiaryRules, beneficiaryForm, { $lazy: true })

const resetForm = () => {
  v$.value.$reset()
  beneficiaryForm.value = { ...beneficiaryInitialState }
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  const payload = {
    ...beneficiaryForm.value,
    allocation_percentage: parseFloat(beneficiaryForm.value.allocation_percentage) / 100,
  }

  loading.value = true
  await props.onSubmit(payload)
  loading.value = false
  resetForm()
}
</script>

<template>
  <VModal :modalStore="modalStore">
    <template #main>
      <VSection label="Add beneficiary">
        <div class="my-6 border-[1px] bg-[#3982AF]/10 p-2 text-sm text-[#3982AF] ring-[#3982AF]/20">
          <span class="font-bold">DISCLAIMER:</span>
          If married, living in a community property state (for example AK, AZ, CA, ID, LA, NV, NM, TX, WA, or WI), and
          want to designate a primary beneficiary other than your spouse, your spouse must agree in writing to your
          designation and you must submit a physical copy of this form by mail or fax. You should consult your legal/tax
          advisor when completing this form, as there may be tax and/or legal consequences to your designation. You have
          the option to list one or more persons (including your estate or a trust, as applicable). If designating
          multiple beneficiaries, indicate the percentage share each should receive, ensuring the total of each adds up
          to 100%. Designations are effective upon hitting submit on this form, and, unless otherwise specified, cancel
          all previous beneficiary designations on file.
        </div>
        <form @submit.prevent="submit">
          <div class="mt-6 space-y-6">
            <VSelect
              v-model="beneficiaryForm.type"
              label="Type"
              :options="[
                { label: 'Individual', value: 'individual' },
                { label: 'Organization', value: 'organization' },
                { label: 'Trust', value: 'trust' },
              ]"
              property="type"
              :required="true"
              :v$="v$"
            />
            <VTextField v-model="beneficiaryForm.name" label="Name" property="name" :required="true" :v$="v$" />
            <VTextField
              v-model="beneficiaryForm.birthdate"
              label="Birthdate"
              type="date"
              property="birthdate"
              :v$="v$"
            />
            <VTextField v-model="beneficiaryForm.relationship" label="Relationship" property="relationship" :v$="v$" />
            <VTextField
              v-model="beneficiaryForm.allocation_percentage"
              label="Allocation Percent"
              placeholder="100%"
              property="allocation_percentage"
              :required="true"
              :v$="v$"
            />
            <VTextField v-model="beneficiaryForm.tax_id" label="SSN / Tax ID" property="tax_id" :v$="v$" />
          </div>
        </form>
      </VSection>
    </template>
    <template #footer>
      <div class="flex items-center justify-between space-x-3">
        <VButton :click="modalStore.close" size="lg">Close</VButton>
        <VButton :click="submit" :loading="loading" class="w-full" size="lg" variant="primary">
          <span>Add Beneficiary</span>
        </VButton>
      </div>
    </template>
  </VModal>
</template>
